import React, { useState, useEffect } from 'react';
import { firestore } from '../firebaseConfig';
import { collection, query, orderBy, onSnapshot, limit, getDocs, startAfter } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { authenticatedFetch, CLOUD_FUNCTION_URLS } from '../utils/cloudFunctions';
import './ScheduledEmails.css'; // Verwende die eigene CSS-Datei

const ScheduledEmails = ({ userId }) => {
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState({});
  const [sendingNow, setSendingNow] = useState({});
  const [expandedEmails, setExpandedEmails] = useState({});
  
  // Pagination states
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreEmails, setHasMoreEmails] = useState(true);
  const [lastEmailDoc, setLastEmailDoc] = useState(null);
  const PAGE_SIZE = 10;

  const functions = getFunctions();

  // Überprüfe, ob Firebase Functions verfügbar sind
  useEffect(() => {
    if (!functions) {
      console.error('❌ Firebase Functions not available');
      setError('Firebase Functions not available. Please check your connection.');
      setLoading(false);
      return;
    }
    
    console.log('✅ Firebase Functions initialized successfully');
  }, [functions]);

  // Teste verschiedene Collection-Pfade (gleiche Logik wie in SmartInbox)
  const testPaths = [
    'world_scheduled_emails',
    'world_scheduled',
    'scheduled_emails',
    'scheduled'
  ];

  const testSortFields = [
    'originalScheduledTime', // Primär: Ursprünglich geplante Zeit (JavaScript Timestamp)
    'scheduledAt',          // Fallback: Geplanter Sendezeitpunkt (Firestore Timestamp)
    'timestamp',            // Fallback: Allgemeiner Zeitstempel
    'date',                // Fallback: Datum-String
    'createdAt'            // Fallback: Erstellungszeitpunkt
  ];

  // Lade erste Seite der geplanten E-Mails
  const loadScheduledEmails = async (loadMore = false) => {
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setScheduledEmails([]);
      setLastEmailDoc(null);
      setHasMoreEmails(true);
    }
    
    try {
      for (const pathPrefix of testPaths) {
        console.log(`📅 [SCHEDULED-EMAILS] Teste Collection-Pfad: ${pathPrefix}/${userId}/emails`);
        
        try {
          const testCollectionRef = collection(firestore, pathPrefix, userId, 'emails');
          const testSnapshot = await getDocs(query(testCollectionRef, limit(1)));
          
          console.log(`📅 [SCHEDULED-EMAILS] ${pathPrefix}: ${testSnapshot.docs.length} Dokumente gefunden`);
          
          if (testSnapshot.docs.length > 0) {
            console.log(`📅 [SCHEDULED-EMAILS] ✅ Gefunden in ${pathPrefix}! Lade E-Mails...`);
            
            // Teste verschiedene Sortierungsfelder
            for (const sortField of testSortFields) {
              console.log(`📅 [SCHEDULED-EMAILS] Teste Sortierung nach: ${sortField}`);
              
              try {
                const collectionRef = collection(firestore, pathPrefix, userId, 'emails');
                
                // Build query with pagination (DESC for scheduled emails - newest/future emails first)
                let emailQuery;
                if (loadMore && lastEmailDoc) {
                  emailQuery = query(
                    collectionRef, 
                    orderBy(sortField, 'desc'), // DESC für neueste/zukünftige E-Mails zuerst
                    startAfter(lastEmailDoc),
                    limit(PAGE_SIZE)
                  );
                  console.log(`📅 [SCHEDULED-EMAILS] Load More Query: orderBy(${sortField}, desc), startAfter, limit(${PAGE_SIZE})`);
                } else {
                  emailQuery = query(
                    collectionRef, 
                    orderBy(sortField, 'desc'), // DESC für neueste/zukünftige E-Mails zuerst
                    limit(PAGE_SIZE)
                  );
                  console.log(`📅 [SCHEDULED-EMAILS] Initial Query: orderBy(${sortField}, desc), limit(${PAGE_SIZE})`);
                }
                
                const snapshot = await getDocs(emailQuery);
                
                                  console.log(`📅 [SCHEDULED-EMAILS] ✅ Erfolgreich ${snapshot.docs.length} E-Mails geladen mit Sortierung nach ${sortField} (desc - neueste/zukünftige zuerst)`);
                  console.log(`📅 [SCHEDULED-EMAILS] Load More Mode: ${loadMore}, Existing Emails: ${scheduledEmails.length}`);
                  
                  if (snapshot.docs.length > 0) {
                    console.log('📅 [SCHEDULED-EMAILS] Erste E-Mail:', snapshot.docs[0].data());
                    console.log('📅 [SCHEDULED-EMAILS] Erste E-Mail timestamp details:', {
                      [sortField]: snapshot.docs[0].data()[sortField],
                      timestamp: snapshot.docs[0].data().timestamp,
                      scheduledAt: snapshot.docs[0].data().scheduledAt,
                      date: snapshot.docs[0].data().date
                    });
                  
                  const emailsArray = snapshot.docs.map(doc => {
                    const data = doc.data();
                    
                    // 🔥 FIXED: Verwende immer den besten verfügbaren Timestamp (nicht sortField abhängig)
                    let timestamp;
                    
                    // Priorität: scheduledAt (Firestore Timestamp) > originalScheduledTime (Number) > timestamp > date
                    if (data.scheduledAt?.seconds) {
                      timestamp = data.scheduledAt.seconds * 1000; // Firestore Timestamp
                    } else if (data.originalScheduledTime && typeof data.originalScheduledTime === 'number') {
                      timestamp = data.originalScheduledTime; // Bereits JavaScript Timestamp
                    } else if (data.timestamp?.seconds) {
                      timestamp = data.timestamp.seconds * 1000; // Firestore Timestamp
                    } else if (data.timestamp && typeof data.timestamp === 'number') {
                      timestamp = data.timestamp; // JavaScript Timestamp
                    } else if (data.date) {
                      timestamp = new Date(data.date).getTime(); // String zu Number
                    } else if (data.createdAt?.seconds) {
                      timestamp = data.createdAt.seconds * 1000; // Firestore Timestamp
                    } else {
                      timestamp = Date.now(); // Fallback
                    }
                    
                    return {
                      id: doc.id,
                      doc: doc, // Store document reference for pagination
                      ...data,
                      timestamp: timestamp,
                      // Berechne verbleibende Zeit
                      remainingTimeMs: Math.max(0, timestamp - Date.now()),
                      remainingTimeMinutes: Math.max(0, Math.ceil((timestamp - Date.now()) / (1000 * 60))),
                      // Status-Informationen
                      isOverdue: timestamp < Date.now(),
                      canCancel: timestamp > Date.now(),
                      // E-Mail-Informationen
                      recipientEmail: data.recipientEmail || data.recipients?.[0] || data.email || 'Unknown',
                      senderEmail: data.senderEmail || data.from || 'Unknown',
                      subject: data.subject || 'No Subject',
                      content: data.content || data.body || 'No Content',
                      businessName: data.businessName || data.business_name || '',
                      emailType: data.emailType || data.type || ''
                    };
                                      });
                    
                    // Extra Sortierung nach Timestamp-Konvertierung (neueste/zukünftige E-Mails zuerst)
                    emailsArray.sort((a, b) => b.timestamp - a.timestamp);
                    console.log('📅 [SCHEDULED-EMAILS] Nach Extra-Sortierung - Erste E-Mail Timestamp (neueste):', new Date(emailsArray[0]?.timestamp).toISOString());
                    console.log('📅 [SCHEDULED-EMAILS] Nach Extra-Sortierung - Letzte E-Mail Timestamp:', new Date(emailsArray[emailsArray.length - 1]?.timestamp).toISOString());
                    
                    // 🔥 FIXED: Update emails with duplicate prevention and pagination
                    if (loadMore) {
                      console.log(`📅 [SCHEDULED-EMAILS] Load More: Adding ${emailsArray.length} emails to existing ${scheduledEmails.length}`);
                      
                      setScheduledEmails(prev => {
                        // Prevent duplicates
                        const existingIds = new Set(prev.map(email => email.id));
                        const newEmails = emailsArray.filter(email => !existingIds.has(email.id));
                        console.log(`📅 [SCHEDULED-EMAILS] Filtered out ${emailsArray.length - newEmails.length} duplicate emails`);
                        
                        // Set pagination based on new emails
                        if (newEmails.length === 0) {
                          setHasMoreEmails(false);
                          console.log(`📅 [SCHEDULED-EMAILS] No new emails found, no more available`);
                        } else if (snapshot.docs.length === PAGE_SIZE && newEmails.length === PAGE_SIZE) {
                          setLastEmailDoc(snapshot.docs[snapshot.docs.length - 1]);
                          setHasMoreEmails(true);
                          console.log(`📅 [SCHEDULED-EMAILS] Full page of new emails (${newEmails.length}), might have more`);
                        } else {
                          setHasMoreEmails(false);
                          console.log(`📅 [SCHEDULED-EMAILS] Partial page or filtered emails (${newEmails.length}), no more available`);
                        }
                        
                        return [...prev, ...newEmails];
                      });
                    } else {
                      console.log(`📅 [SCHEDULED-EMAILS] Initial load: Setting ${emailsArray.length} emails`);
                      setScheduledEmails(emailsArray);
                      
                      // Standard pagination for initial load
                      if (snapshot.docs.length === PAGE_SIZE) {
                        setLastEmailDoc(snapshot.docs[snapshot.docs.length - 1]);
                        setHasMoreEmails(true);
                        console.log(`📅 [SCHEDULED-EMAILS] Initial full page loaded (${PAGE_SIZE}), more emails available`);
                      } else {
                        setHasMoreEmails(false);
                        console.log(`📅 [SCHEDULED-EMAILS] Initial partial page loaded (${snapshot.docs.length}), no more emails`);
                      }
                    }
                  
                  console.log(`📅 [SCHEDULED-EMAILS] ${emailsArray.length} scheduled emails loaded and processed`);
                  
                  if (loadMore) {
                    setLoadingMore(false);
                  } else {
                    setLoading(false);
                  }
                  
                  return; // Erfolgreich geladen, stoppe weitere Tests
                } else {
                  // Keine E-Mails in dieser Abfrage, aber das könnte bei einem anderen sortField funktionieren
                  console.log(`📅 [SCHEDULED-EMAILS] Keine E-Mails gefunden mit ${sortField}, versuche nächstes sortField`);
                  continue;
                }
                
              } catch (error) {
                console.log(`📅 [SCHEDULED-EMAILS] Sortierung nach ${sortField} fehlgeschlagen:`, error.message);
                continue;
              }
            }
          }
        } catch (error) {
          console.log(`📅 [SCHEDULED-EMAILS] Fehler bei ${pathPrefix}:`, error.message);
          continue;
        }
      }
      
      // Wenn keine Collection gefunden wurde
      console.log('📅 [SCHEDULED-EMAILS] ❌ Keine scheduled emails Collection gefunden');
      if (!loadMore) {
        // Nur bei initial load das Array leeren
        setScheduledEmails([]);
      }
      setHasMoreEmails(false);
      
    } catch (error) {
      console.error('📅 [SCHEDULED-EMAILS] Allgemeiner Fehler beim Laden:', error);
      setError(`Error loading scheduled emails: ${error.message}`);
      // Bei Fehler auch hasMoreEmails auf false setzen
      setHasMoreEmails(false);
    } finally {
      if (loadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Lade geplante E-Mails aus Firestore (neue Logik)
  useEffect(() => {
    if (!userId) return;

    console.log('📅 [SCHEDULED-EMAILS] Loading scheduled emails from Firestore...');
    console.log('📅 [SCHEDULED-EMAILS] UserId:', userId);
    
    loadScheduledEmails();
  }, [userId]);

  // Load more emails function
  const handleLoadMore = () => {
    if (!loadingMore && hasMoreEmails) {
      loadScheduledEmails(true);
    }
  };

  // Funktion zum Stornieren einer geplanten E-Mail
  const handleCancelEmail = async (scheduledEmailId) => {
    if (!scheduledEmailId) {
      console.error('❌ No scheduledEmailId provided');
      return;
    }
    
    console.log(`🚫 Starting cancel process for email: ${scheduledEmailId}`);
    console.log(`📋 User ID: ${userId}`);
    
    setCancelling(prev => ({ ...prev, [scheduledEmailId]: true }));
    
    try {
      console.log(`🔄 Calling cancelScheduledEmail function...`);
      
      // Direkter HTTP-Request zur Firebase Function
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.cancelScheduledEmail(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          scheduledEmailId
        })
      });
      
      console.log(`📨 Function response status:`, response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log(`📨 Function response data:`, result);
      
      if (result.success) {
        console.log(`✅ Successfully cancelled scheduled email: ${scheduledEmailId}`);
        setScheduledEmails(prev => prev.filter(e => e.id !== scheduledEmailId));
      } else {
        console.error(`❌ Function returned unsuccessful result:`, result);
        throw new Error(result.details || result.error || 'Failed to cancel email');
      }
    } catch (error) {
      console.error(`❌ Error cancelling scheduled email:`, error);
      console.error(`❌ Error details:`, {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      
      // Verbesserte Fehlermeldung basierend auf dem Fehlertyp
      let errorMessage = 'Error cancelling email';
      if (error.message.includes('404')) {
        errorMessage = 'Email not found - it may have already been sent or cancelled';
      } else if (error.message.includes('403')) {
        errorMessage = 'Permission denied - you may not have access to cancel this email';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'Network error - please try again';
      } else {
        errorMessage = `Error: ${error.message}`;
      }
      
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    } finally {
      setCancelling(prev => ({ ...prev, [scheduledEmailId]: false }));
    }
  };

  // Sende eine geplante E-Mail SOFORT (vor dem geplanten Zeitpunkt).
  // Backend sorgt dafür, dass der Cloud Task gelöscht wird, bevor wir
  // selbst senden — d.h. die E-Mail kann nicht doppelt rausgehen.
  const handleSendNow = async (scheduledEmailId, recipientEmail) => {
    if (!scheduledEmailId) {
      console.error('❌ No scheduledEmailId provided');
      return;
    }

    const confirmMsg = recipientEmail
      ? `Send this email to ${recipientEmail} now? This cannot be undone.`
      : 'Send this email now? This cannot be undone.';
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmMsg)) {
      return;
    }

    console.log(`⚡ Starting send-now for email: ${scheduledEmailId}`);

    setSendingNow(prev => ({ ...prev, [scheduledEmailId]: true }));

    try {
      const response = await authenticatedFetch(
        CLOUD_FUNCTION_URLS.sendScheduledEmailNow(),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            scheduledEmailId,
          }),
        }
      );

      const result = await response.json().catch(() => ({}));
      console.log('⚡ sendScheduledEmailNow response:', response.status, result);

      if (!response.ok || !result?.success) {
        const reasonCode = result?.error || `HTTP_${response.status}`;
        const detail = result?.details || 'Unknown error';
        let userMsg = `Could not send now (${reasonCode}): ${detail}`;
        if (reasonCode === 'already_in_progress') {
          userMsg = 'This email is already being sent — please wait a moment.';
        } else if (reasonCode === 'already_sent') {
          userMsg = 'This email has already been sent.';
        } else if (reasonCode === 'cloud_task_not_found' || reasonCode === 'cloud_task_body_unavailable') {
          userMsg = 'The scheduled email is no longer in the queue (it may have already been sent).';
        } else if (reasonCode === 'no_cloud_task') {
          userMsg = 'This scheduled email is too old to trigger early. Please cancel and resend manually.';
        }
        throw new Error(userMsg);
      }

      // Erfolg — entferne das item aus der Liste
      setScheduledEmails(prev => prev.filter(e => e.id !== scheduledEmailId));

      if (result.skipped) {
        console.log('ℹ️ Send-now request was skipped (race), email already delivered');
      }
    } catch (err) {
      console.error('❌ Error sending email now:', err);
      setError(err.message || 'Could not send email now');
      setTimeout(() => setError(null), 6000);
    } finally {
      setSendingNow(prev => ({ ...prev, [scheduledEmailId]: false }));
    }
  };

  // Funktion zum Formatieren der verbleibenden Zeit
  const formatRemainingTime = (remainingTimeMs) => {
    if (remainingTimeMs <= 0) return 'Overdue';
    
    const minutes = Math.ceil(remainingTimeMs / (1000 * 60));
    
    if (minutes < 60) {
      return `${minutes}m`;
    } else if (minutes < 1440) { // weniger als 24 Stunden
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    } else {
      const days = Math.floor(minutes / 1440);
      const remainingHours = Math.floor((minutes % 1440) / 60);
      return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
    }
  };

  // Funktion zum Formatieren des geplanten Zeitpunkts
  const formatScheduledTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    
    // Prüfe, ob es heute ist
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return `Today ${date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleString('de-DE', { 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  // Funktion zum Extrahieren des Namens aus der E-Mail-Adresse
  const extractNameFromEmail = (email) => {
    if (!email) return 'Unknown';
    return email.split('@')[0];
  };

  // NEU: Funktion zum Togglen der E-Mail-Erweiterung
  const toggleEmailExpansion = (emailId) => {
    setExpandedEmails(prev => ({
      ...prev,
      [emailId]: !prev[emailId]
    }));
  };

  if (loading) {
    return (
      <div className="scheduledEmails_skeleton_container">
        <div className="scheduledEmails_skeleton_header" />
        {[1, 2, 3].map(i => (
          <div key={i} className="scheduledEmails_skeleton_card">
            <div className="scheduledEmails_skeleton_left">
              <div className="scheduledEmails_skeleton_line" style={{ width: '60px', height: '10px' }} />
              <div className="scheduledEmails_skeleton_line" style={{ width: '180px', height: '14px' }} />
              <div className="scheduledEmails_skeleton_line" style={{ width: '240px', height: '12px' }} />
              <div className="scheduledEmails_skeleton_line" style={{ width: '140px', height: '10px' }} />
            </div>
            <div className="scheduledEmails_skeleton_right">
              <div className="scheduledEmails_skeleton_line" style={{ width: '90px', height: '12px' }} />
              <div className="scheduledEmails_skeleton_line" style={{ width: '60px', height: '20px', borderRadius: '100px' }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (scheduledEmails.length === 0 && !loading && !loadingMore) {
    return (
      <div className="scheduledEmails_empty">
        <div className="scheduledEmails_empty_illustration">
          <img 
            src="/img/clock-schedule.svg" 
            alt="No Scheduled Emails" 
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/img/mailerle.webp";
            }}
            style={{ maxWidth: "80px", maxHeight: "80px" }}
          />
        </div>
        <h3 className="scheduledEmails_empty_title">No scheduled emails</h3>
        <p className="scheduledEmails_empty_description">
          When you schedule emails for later delivery, they will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="scheduledEmails_container">
      {/* Header mit Statistiken */}
      <div className="scheduledEmails_header">
        <div className="scheduledEmails_stats">
          <div className="scheduledEmails_stat_item">
            <span className="scheduledEmails_stat_number">{scheduledEmails.length}</span>
            <span className="scheduledEmails_stat_label">Scheduled Emails</span>
          </div>
          
          {scheduledEmails.length > 0 && (
            <>
              <div className="scheduledEmails_stat_item">
                <span className="scheduledEmails_stat_number">
                  {scheduledEmails.filter(email => email.canCancel).length}
                </span>
                <span className="scheduledEmails_stat_label">Cancellable</span>
              </div>
              
              <div className="scheduledEmails_stat_item">
                <span className="scheduledEmails_stat_number">
                  {scheduledEmails.filter(email => email.isOverdue).length}
                </span>
                <span className="scheduledEmails_stat_label">Overdue</span>
              </div>
              
              <div className="scheduledEmails_stat_item">
                <span className="scheduledEmails_stat_number">
                  {scheduledEmails.filter(email => !email.isOverdue && email.canCancel).length}
                </span>
                <span className="scheduledEmails_stat_label">Pending</span>
              </div>
            </>
          )}
        </div>
        
        {scheduledEmails.length > 0 && (
          <div className="scheduledEmails_next_email">
            <span className="scheduledEmails_next_label">Next email in:</span>
            <span className="scheduledEmails_next_time">
              {formatRemainingTime(Math.min(...scheduledEmails.map(email => email.remainingTimeMs)))}
            </span>
          </div>
        )}
      </div>

      {error && (
        <div className="scheduledEmails_error">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Close</button>
        </div>
      )}
      
      {scheduledEmails.map((email) => (
        <div key={email.id} className={`scheduledEmails_email_card ${email.isOverdue ? 'overdue' : ''} ${expandedEmails[email.id] ? 'expanded' : ''}`}>
          <div 
            className="scheduledEmails_email_header"
            onClick={() => toggleEmailExpansion(email.id)}
            style={{ cursor: 'pointer' }}
          >
            <div className="scheduledEmails_email_left">
              <div className="scheduledEmails_direction_label">
                Scheduled to:
              </div>
              <div className="scheduledEmails_recipient_info">
                <span className="scheduledEmails_recipient_name">
                  {extractNameFromEmail(email.recipientEmail)}
                </span>
                
                {/* E-Mail-Typ Badge */}
                {email.emailType && (
                  <span className={`scheduledEmails_type_badge ${email.emailType}`}>
                    {email.emailType === 'intro' && 'Introduction'}
                    {email.emailType === 'follow_up_1' && 'Follow-up 1'}
                    {email.emailType === 'follow_up_2' && 'Follow-up 2'}
                    {email.emailType === 'follow_up_3' && 'Follow-up 3'}
                    {email.emailType === 'email_reply' && 'Reply'}
                    {!['intro', 'follow_up_1', 'follow_up_2', 'follow_up_3', 'email_reply'].includes(email.emailType) && email.emailType}
                  </span>
                )}
                
                {/* Reply Badge */}
                {email.isReply && (
                  <span className="scheduledEmails_reply_badge">
                    Reply
                  </span>
                )}
              </div>
              
              <div className="scheduledEmails_subject">
                {email.subject}
              </div>
              
              <div className="scheduledEmails_recipient_email">
                {email.recipientEmail}
              </div>
            </div>
            
            <div className="scheduledEmails_email_right">
              <div className="scheduledEmails_time_info">
                <div className="scheduledEmails_scheduled_time">
                  📅 {formatScheduledTime(email.timestamp)}
                </div>
                <div className={`scheduledEmails_remaining_time ${email.isOverdue ? 'overdue' : ''}`}>
                  {formatRemainingTime(email.remainingTimeMs)}
                </div>
              </div>
              
              {/* Status Badge */}
              <div className="scheduledEmails_status_container">
                <span className={`scheduledEmails_status_badge ${email.isOverdue ? 'overdue' : 'scheduled'}`}>
                  {email.isOverdue ? 'Overdue' : 'Scheduled'}
                </span>
              </div>
              
              {/* Expand/Collapse Indicator */}
              <div className="scheduledEmails_expand_indicator">
                {expandedEmails[email.id] ? '▼' : '▶'}
              </div>
            </div>
          </div>

          {/* Erweiterte Details - nur anzeigen wenn erweitert */}
          {expandedEmails[email.id] && (
            <div className="scheduledEmails_expanded_content">
              {/* Zeige den vollständigen Inhalt mit HTML-Formatierung */}
              <div className="scheduledEmails_preview">
                {email.content ? (
                  <div 
                    dangerouslySetInnerHTML={{ 
                      __html: email.content 
                    }}
                    style={{
                      maxHeight: '400px',
                      overflowY: 'auto',
                      lineHeight: '1.6',
                      whiteSpace: 'pre-wrap'
                    }}
                  />
                ) : (
                  'No preview available'
                )}
              </div>
              
              {/* Zusätzliche Informationen */}
              <div className="scheduledEmails_details">
                <div className="scheduledEmails_detail_item">
                  <span className="scheduledEmails_detail_label">From:</span>
                  <span className="scheduledEmails_detail_value">{email.senderEmail}</span>
                </div>
                
                {email.businessName && (
                  <div className="scheduledEmails_detail_item">
                    <span className="scheduledEmails_detail_label">Business:</span>
                    <span className="scheduledEmails_detail_value">{email.businessName}</span>
                  </div>
                )}
                
                {email.randomFactor && (
                  <div className="scheduledEmails_detail_item">
                    <span className="scheduledEmails_detail_label">Delay Factor:</span>
                    <span className="scheduledEmails_detail_value">
                      {(email.randomFactor * 100).toFixed(0)}% ({email.randomizedQueueDuration}min)
                    </span>
                  </div>
                )}
                
                {email.sleepTimeAdjusted && (
                  <div className="scheduledEmails_detail_item">
                    <span className="scheduledEmails_detail_label">Sleep Time:</span>
                    <span className="scheduledEmails_detail_value">
                      Adjusted ({email.sleepTimeStart} - {email.sleepTimeEnd})
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="scheduledEmails_actions">
                {email.canCancel && (
                  <button
                    className="scheduledEmails_send_now_button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendNow(email.id, email.recipientEmail);
                    }}
                    disabled={sendingNow[email.id] || cancelling[email.id]}
                    title="Send this email now (skip the scheduled wait)"
                  >
                    {sendingNow[email.id] ? (
                      <>
                        <span className="scheduledEmails_spinner"></span>
                        Sending…
                      </>
                    ) : (
                      <>
                        ⚡ Send now
                      </>
                    )}
                  </button>
                )}

                {email.canCancel && (
                  <button 
                    className="scheduledEmails_cancel_button"
                    onClick={(e) => {
                      e.stopPropagation(); // Verhindere das Togglen beim Klick auf den Button
                      handleCancelEmail(email.id);
                    }}
                    disabled={cancelling[email.id] || sendingNow[email.id]}
                    title="Cancel scheduled email"
                  >
                    {cancelling[email.id] ? (
                      <>
                        <span className="scheduledEmails_spinner"></span>
                        Cancelling...
                      </>
                    ) : (
                      <>
                        🚫 Cancel
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Load More Button */}
      {hasMoreEmails && (
        <div className="scheduledEmails_load_more_container">
          <button 
            className="scheduledEmails_load_more_button"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? (
              <>
                <span className="scheduledEmails_spinner"></span>
                Loading more emails...
              </>
            ) : (
              <>
                Load More Emails
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '8px' }}>
                  <path d="M12 5v14M5 12l7 7 7-7"/>
                </svg>
              </>
            )}
          </button>
        </div>
      )}

      {/* Footer Info */}
      {scheduledEmails.length > 0 && (
        <div className="scheduledEmails_footer_info">
          <span>Showing {scheduledEmails.length} emails</span>
          {hasMoreEmails && <span> • More emails available</span>}
        </div>
      )}
    </div>
  );
};

export default ScheduledEmails; 